import { backgroundColors } from '@/colors';
import Fab from '@/components/elements/forms/buttons/Fab/Fab';
import { ListItem } from '@/components/elements/lists';
import { InitialsAvatar } from '@/components/elements/redesign/Avatar';
import Icon from '@/components/icons/Icon';
import LogoutModal from '@/components/modules/modals/LogoutModal';
import { NAVIGATION_TRIGGER } from '@/constants/navigation';
import { getUserInitials } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { _s } from '@/locale';
import { Fragment } from 'react/jsx-runtime';
import { ACCOUNT_NAV_ITEMS } from './AccountNavigation.constants';
import { AccountNavigationContextType } from './AccountNavigation.hooks';
import { AccountNavItemIdentifier } from './AccountNavigation.types';

const Header = ({
  showConfirmLogoutModal,
  onAbortLogout,
  onClose,
  onLogoutClick,
  onLogoutConfirm,
}: Pick<
  AccountNavigationContextType,
  'onAbortLogout' | 'onClose' | 'onLogoutClick' | 'onLogoutConfirm' | 'showConfirmLogoutModal'
>) => {
  const user = useAppSelector((state) => state.users?.user);
  const userInitials = user && getUserInitials(user);
  const name = `${user?.about?.givenName ?? ''} ${user?.about?.familyName ?? ''}`;

  return (
    <Fragment>
      <div className="py-md space-y-md">
        <div className="gap-xs flex items-start">
          <InitialsAvatar initials={userInitials} size="xl" variant="default" bgColor={backgroundColors.bg_brand} />
          <div className="gap-md ml-auto flex items-center">
            <Fab
              variant="secondary"
              icon={<Icon variant="logout" />}
              onClick={onLogoutClick}
              size="md"
              aria-label={_s('logOut')}
            />
            <Fab
              variant="secondary"
              icon={<Icon variant="close" />}
              onClick={onClose}
              size="md"
              aria-label={_s('close')}
            />
          </div>
        </div>
        <h2 className="text-h-m text-fg_primary">{name}</h2>
      </div>
      <LogoutModal
        isOpen={showConfirmLogoutModal}
        onClick={onLogoutConfirm}
        onClose={onAbortLogout}
        triggerSource={NAVIGATION_TRIGGER.NAV_DRAWER}
      />
    </Fragment>
  );
};

const Content = (props: Pick<AccountNavigationContextType, 'onNavigationItemClick'>) => {
  const user = useAppSelector((state) => state.users?.user);

  return (
    <Fragment>
      <ul className="space-y-xs flex flex-col">
        {ACCOUNT_NAV_ITEMS.map((item) => {
          const missingActiveCard = item.identifier === AccountNavItemIdentifier.PaymentMethods && !user?.activeCoF;
          return (
            <ListItem.Base
              key={item.identifier}
              onClick={() => props.onNavigationItemClick(item.identifier)}
              to={{ pathname: item.to, state: { fromAccountNavigation: true } }}
              rightSlot={
                <span className="p-xs flex items-center">
                  {missingActiveCard && <Icon variant="error" color="fg_negative" size="sm" />}
                </span>
              }
              className="min-h-[56px]">
              <span className="gap-xs flex items-center">
                <Icon variant={item.icon} />
                <span className="flex flex-col">
                  <span className="text-m">{item.label}</span>
                  {missingActiveCard && <span className="text-s text-fg_negative">{_s('missingActiveCard')}</span>}
                </span>
              </span>
            </ListItem.Base>
          );
        })}
      </ul>
    </Fragment>
  );
};

function AccountNavigation(props: AccountNavigationContextType) {
  return (
    <Fragment>
      <AccountNavigation.Header {...props} />
      <AccountNavigation.Content {...props} />
    </Fragment>
  );
}

AccountNavigation.Header = Header;
AccountNavigation.Content = Content;

export default AccountNavigation;
